import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Button, TextField, Chip, IconButton, MenuItem, Select,
  InputAdornment, TablePagination, Tooltip, CircularProgress, Dialog, DialogContent, DialogActions
} from '@mui/material';
import { Add, Search, Edit, Transform, Cancel, Print, Email, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API } from '../context/AuthContext';
import { STATUS_COLORS, STATUS_LABELS, fmt } from '../utils';
import { useSnackbar } from 'notistack';
import { printQuote } from '../components/PrintDocument';
import SendQuoteEmailDialog from '../components/SendQuoteEmailDialog';

const STATUSES = ['', 'borrador', 'enviada', 'aceptada', 'rechazada', 'facturada', 'cancelada'];

export default function QuotesPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [emailDialog, setEmailDialog] = useState({ open: false, quote: null });

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    const { data } = await API.get('/quotes', { params: { search, status, page: page + 1, limit: 15 } });
    setQuotes(data.quotes); setTotal(data.total);
    setLoading(false);
  }, [search, status, page]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const convertToInvoice = async (id, number) => {
    if (!window.confirm(`¿Convertir cotización ${number} a factura?`)) return;
    try {
      await API.post(`/quotes/${id}/convert`);
      enqueueSnackbar('Factura generada exitosamente', { variant: 'success' });
      fetchQuotes();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al convertir', { variant: 'error' });
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await API.patch(`/quotes/${id}/status`, { status: newStatus });
      fetchQuotes();
    } catch (err) {
      enqueueSnackbar('Error al actualizar estado', { variant: 'error' });
    }
  };

  const deleteQuote = async (id, number) => {
    if (!window.confirm(`¿Eliminar PERMANENTEMENTE la cotización ${number}? Esta acción no se puede deshacer.`)) return;
    try {
      await API.delete(`/quotes/${id}`);
      enqueueSnackbar('Cotización eliminada', { variant: 'success' });
      fetchQuotes();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al eliminar', { variant: 'error' });
    }
  };

  const handlePrint = async (id) => {
    try {
      const res = await API.get(`/quotes/${id}`, { headers: { 'Cache-Control': 'no-cache' } });
      printQuote(res.data);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Error desconocido';
      enqueueSnackbar(`Error al imprimir: ${msg}`, { variant: 'error' });
      console.error('Print error:', err);
    }
  };

  const handleOpenEmailDialog = async (id) => {
    try {
      const res = await API.get(`/quotes/${id}`, { headers: { 'Cache-Control': 'no-cache' } });
      setEmailDialog({ open: true, quote: res.data });
    } catch (err) {
      enqueueSnackbar('Error al cargar cotización', { variant: 'error' });
    }
  };

  return (
    <Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E1B2E' }}>Cotizaciones</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{total} cotizaciones en total</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/cotizaciones/nueva')}>
          Nueva cotización
        </Button>
      </Box>

      <Card>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField placeholder="Buscar por número, cliente…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }} />
          <Select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} displayEmpty size="small" sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos los estados</MenuItem>
            {STATUSES.filter(Boolean).map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
          </Select>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : quotes.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sin cotizaciones</TableCell></TableRow>
            ) : quotes.map(q => (
              <TableRow key={q._id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/cotizaciones/${q._id}/editar`)}>
                    {q.number}
                  </Typography>
                </TableCell>
                <TableCell><Typography variant="body2">{fmt.date(q.date || q.createdAt)}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{q.clientSnapshot?.name || q.client?.name}</Typography>
                </TableCell>
                <TableCell><Typography variant="body2">{q.seller?.name || '-'}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700}>{fmt.currency(q.total)}</Typography></TableCell>
                <TableCell><Chip label={STATUS_LABELS[q.status]} color={STATUS_COLORS[q.status]} size="small" /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Enviar por correo">
                    <IconButton size="small" color="primary" onClick={() => handleOpenEmailDialog(q._id)}>
                      <Email fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Imprimir / PDF">
                    <IconButton size="small" color="primary" onClick={() => handlePrint(q._id)}>
                      <Print fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => navigate(`/cotizaciones/${q._id}/editar`)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  {['borrador','enviada','aceptada'].includes(q.status) && (
                    <Tooltip title="Convertir a factura">
                      <IconButton size="small" color="success" onClick={() => convertToInvoice(q._id, q.number)}>
                        <Transform fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!['facturada','cancelada'].includes(q.status) && (
                    <Tooltip title="Cancelar">
                      <IconButton size="small" color="error" onClick={() => updateStatus(q._id, 'cancelada')}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {q.status !== 'facturada' && (
                    <Tooltip title="Eliminar permanentemente">
                      <IconButton size="small" color="error" onClick={() => deleteQuote(q._id, q.number)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={15}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[15]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
      </Card>

      <SendQuoteEmailDialog
        open={emailDialog.open}
        quote={emailDialog.quote}
        onClose={() => setEmailDialog({ open: false, quote: null })}
        onSent={fetchQuotes}
      />
    </Box>
  );
}
